import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ShareCheckKind, ShareLinkCreateResult, ShareLinkSummary } from "shared";
import { api, ApiError } from "../api";
import { CopyButton } from "./CopyButton";

type EmailRow = { id: string; status: string; sentAt: string | null };
export const shareLinksKey = (id: string) => ["worksheet-share", id] as const;
export const emailsKey = (id: string) => ["worksheet-emails", id] as const;

const CHECK_TEXT: Record<ShareCheckKind, string> = { zip3: "the last 3 digits of their ZIP code", phone4: "the last 4 digits of their phone number" };
const EMAIL_TEXT: Record<string, string> = { sent: "Sent", failed: "Failed", queued: "Queued" };
const NO_EMAIL = "This customer has no email address — create a link and share it yourself.";

type LinkState = "Active" | "Expired" | "Revoked" | "Locked";
const linkState = (l: ShareLinkSummary): LinkState =>
  l.revokedAt ? "Revoked" : l.locked ? "Locked" : Date.parse(l.expiresAt) <= Date.now() ? "Expired" : "Active";
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : "");
const errText = (e: unknown, fallback: string) => (e instanceof ApiError && e.message ? e.message : fallback);

/**
 * Send / share-link controls for a ready or sent worksheet. The one-time url lives only in this component's state:
 * never in the query cache, storage, the address bar, the title or the console.
 */
export function ShareControls({ id, hasEmail, blockedReason, onSent }: {
  id: string; hasEmail: boolean; blockedReason: string | null; onSent: () => Promise<void> | void;
}) {
  const qc = useQueryClient();
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [created, setCreated] = useState<ShareLinkCreateResult | null>(null);
  const [checkKind, setCheckKind] = useState<"" | ShareCheckKind>("");
  const links = useQuery({ queryKey: shareLinksKey(id), queryFn: () => api<{ links: ShareLinkSummary[] }>(`/api/worksheets/${id}/share`) });
  const emails = useQuery({ queryKey: emailsKey(id), queryFn: () => api<{ emails: EmailRow[] }>(`/api/worksheets/${id}/emails`) });
  const refresh = () => Promise.all([
    qc.invalidateQueries({ queryKey: shareLinksKey(id) }), qc.invalidateQueries({ queryKey: emailsKey(id) }),
  ]);

  const sending = useRef(false);
  const send = useMutation({
    mutationFn: async () => {
      if (sending.current) return; // a second click before the first has re-rendered
      sending.current = true;
      try { await api(`/api/worksheets/${id}/send`, { method: "POST" }); } finally { sending.current = false; }
    },
    onMutate: () => { setNotice(""); setError(""); },
    // The email is out: refreshing the lists is best effort and must never turn this into a send error.
    onSuccess: () => {
      setNotice("Email sent. The link works for the number of days set by your administrator.");
      void Promise.allSettled([refresh(), Promise.resolve().then(onSent)]);
    },
    onError: (e) => { setError(errText(e, "Could not send the email")); void refresh(); },
  });
  // Not a mutation: a mutation keeps its result (the url) in the MutationCache. The url lives only in `created`.
  const [creating, setCreating] = useState(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const createLink = async () => {
    if (creating) return;
    setCreating(true); setCreated(null); setNotice(""); setError("");
    try {
      const r = await api<ShareLinkCreateResult>(`/api/worksheets/${id}/share`, { method: "POST", body: checkKind ? { checkKind } : {} });
      if (mounted.current) { setCreated(r); void refresh(); }
    } catch (e) {
      if (mounted.current) setError(errText(e, "Could not create the link"));
    } finally {
      if (mounted.current) setCreating(false);
    }
  };
  const revoke = useMutation({
    mutationFn: (linkId: string) => api(`/api/share-links/${linkId}/revoke`, { method: "POST" }),
    onMutate: () => { setNotice(""); setError(""); },
    onSuccess: () => { setNotice("Link revoked."); void refresh(); },
    onError: (e) => setError(errText(e, "Could not revoke the link")),
  });
  const confirmRevoke = (l: ShareLinkSummary) => {
    if (window.confirm(`Revoke the link created ${new Date(l.createdAt).toLocaleString()}? The customer will no longer be able to open it.`)) revoke.mutate(l.id);
  };

  const blocked = blockedReason !== null;
  const busy = send.isPending || creating;
  const sentCount = (emails.data?.emails ?? []).filter((e) => e.status === "sent").length;

  return (
    <section className="mt-4 border-t border-slate-200 pt-3 dark:border-slate-800" aria-label="Send worksheet to the customer">
      <h4 className="mb-2 text-sm font-semibold">Send to the customer</h4>
      {blocked && <p className="mb-2 text-sm text-slate-600 dark:text-slate-400">{blockedReason}</p>}
      {!hasEmail && <p className="mb-2 text-sm text-slate-600 dark:text-slate-400">{NO_EMAIL}</p>}
      <div className="flex flex-wrap items-end gap-2">
        <button type="button" className="btn-primary" disabled={blocked || busy || !hasEmail} onClick={() => send.mutate()}>Send link by email</button>
        <div>
          <label className="label" htmlFor={`check-${id}`}>Customer check</label>
          <select id={`check-${id}`} className="input" value={checkKind} disabled={blocked || busy} onChange={(e) => setCheckKind(e.target.value as "" | ShareCheckKind)}>
            <option value="">Default check</option>
            <option value="zip3">Last 3 digits of ZIP</option>
            <option value="phone4">Last 4 digits of phone</option>
          </select>
        </div>
        <button type="button" className="btn-ghost border border-slate-300" disabled={blocked || busy} onClick={() => void createLink()}>Create link to share yourself</button>
      </div>

      <div role="status" aria-live="polite" className="mt-2 text-sm">{notice}</div>
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}

      {created && (
        <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950">
          <p className="mb-2 font-medium">This link is shown only now. Anyone with the link and the check digits can open the worksheet.</p>
          <label className="label" htmlFor={`url-${id}`}>Secret link</label>
          <div className="flex flex-wrap items-center gap-2">
            <input id={`url-${id}`} className="input min-w-0 flex-1" readOnly value={created.url} onFocus={(e) => e.currentTarget.select()} />
            <CopyButton text={created.url} label="Copy link" ariaLabel="Copy link" />
            <button type="button" className="btn-ghost" onClick={() => setCreated(null)}>Hide link</button>
          </div>
          <p className="mt-2">The customer will be asked for {CHECK_TEXT[created.checkKind]}. Expires {day(created.expiresAt)}.</p>
        </div>
      )}

      <h4 className="mb-1 mt-3 text-sm font-semibold">Links</h4>
      {links.isError && <p className="text-sm text-red-600">Could not load the links.</p>}
      {(links.data?.links ?? []).length === 0 && !links.isLoading && <p className="text-sm text-slate-500">No links yet.</p>}
      <ul className="space-y-1 text-sm">
        {(links.data?.links ?? []).map((l) => {
          const st = linkState(l);
          return (
            <li key={l.id} aria-label={`Link created ${day(l.createdAt)}`} className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{st}</span>
              <span>Expires {day(l.expiresAt)}</span>
              <span>{l.openCount > 0 ? `Opened ${l.openCount} ${l.openCount === 1 ? "time" : "times"}` : "Not opened"}</span>
              {st === "Active" && <button type="button" className="btn-ghost" disabled={revoke.isPending} aria-label={`Revoke link created ${day(l.createdAt)}`} onClick={() => confirmRevoke(l)}>Revoke</button>}
            </li>
          );
        })}
      </ul>

      <h4 className="mb-1 mt-3 text-sm font-semibold">Emails: {sentCount} sent</h4>
      <ul className="space-y-1 text-sm">
        {(emails.data?.emails ?? []).map((e) => (
          <li key={e.id} aria-label={`Email ${e.status}`}>{EMAIL_TEXT[e.status] ?? e.status}{e.sentAt ? ` — ${day(e.sentAt)}` : ""}</li>
        ))}
      </ul>
    </section>
  );
}
